import { MetricField, MetricUnit } from '@/__generated__/graphql';
import { useAnalyticsEventTracker } from '@/gaEvents';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import AlertIcon from '@/icons/amaruNewIcons/AlertIcon';
import ReportsIcon from '@/icons/amaruNewIcons/ReportsIcon';
import { useSelector } from '@/store';
import { isMetricUnit } from '@/utils/metricsGuards';
import useAccess from '@/hooks/useAccess';
import { DevicesAnalysisDevice } from '@features/devicesAnalysis/hooks/useDevicesAnalysis';
import { dataToTable } from '@features/devicesAnalysis/utils/dataToTable';
import ReportProblemDialog from '@features/reportProblem';
import { getMetricUnits } from '@features/reports/utils/metricHelpers';
import DateTimeRangePicker from '@features/standardDesign/components/DateTimeRangePicker/index';
import { RangeTimeValue } from '@features/standardDesign/components/DateTimeRangePicker/types';
import { formatDateTimeRangeLabel } from '@features/standardDesign/components/DateTimeRangePicker/utils';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Download } from '@mui/icons-material';
import { Autocomplete, Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import { TransitionStartFunction, useState, type FC } from 'react';
import { useDispatch } from 'react-redux';
import DeviceMenu from 'src/features/deviceMenu/components/DeviceMenu';
import { useFormatters } from 'src/hooks/useFormatters';
import GearIcon from 'src/icons/amaruNewIcons/GearIcon';
import { setParameters, TelemetryKey } from '../../slices/devicesAnalysisSlice';

type TelemetryData = Record<string, unknown>;

interface MetricConfig {
  name: MetricField;
  unit?: MetricUnit;
}

const windowOptions = [
  { label: msg`Dato bruto`, window: null },
  { label: msg`1 minuto`, window: '1m' },
  { label: msg`5 minutos`, window: '5m' },
  { label: msg`10 minutos`, window: '10m' },
  { label: msg`15 minutos`, window: '15m' },
  { label: msg`30 minutos`, window: '30m' },
  { label: msg`1 hora`, window: '1h' },
  { label: msg`6 horas`, window: '6h' },
  { label: msg`12 horas`, window: '12h' },
  { label: msg`1 día`, window: '1d' },
  { label: msg`3 días`, window: '3d' },
];

interface Props {
  device: DevicesAnalysisDevice;
  startTransition: TransitionStartFunction;
}

const Toolbar: FC<Props> = ({ device, startTransition }) => {
  const { setModalState: setDownloadDataModalState } = useGlobalModal('downloadData');
  const { setModalState: setCreateReportModalState } = useGlobalModal('createReports');
  const { _, i18n } = useLingui();
  const { formatDateTimeRange } = useFormatters();
  const dispatch = useDispatch();
  const { clickEvent, selectorEvent } = useAnalyticsEventTracker('Station Analysis');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const { hasAccess } = useAccess();
  const { parameters, telemetry, data } = useSelector((state) => state.devices_analysis_store);

  const handleReport = () => {
    clickEvent({ label: 'report-problem' });
    setShowReportDialog(true);
  };

  const handleChangeWindow = (value: string | null) => {
    selectorEvent({ label: 'window', value: value ?? 'Dato bruto' });
    startTransition(() => {
      dispatch(setParameters({ window: value }));
    });
  };

  const handleChangeTimeRange = (value: RangeTimeValue) => {
    selectorEvent({
      label: 'period',
      value: formatDateTimeRangeLabel(value, i18n, formatDateTimeRange),
    });
    startTransition(() => {
      dispatch(setParameters({ start: value.start, stop: value.stop }));
    });
  };

  return (
    <>
      <ReportProblemDialog
        device={{
          id: device.id,
          name: device.profile.name,
          type: device.profile.type,
          organization_name: device?.profile?.organization?.name,
        }}
        show={showReportDialog}
        handleClose={() => setShowReportDialog(false)}
      />
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: 'repeat(2, 1fr) auto',
        }}
        gap={2}
      >
        <DateTimeRangePicker
          value={{ start: parameters.start, stop: parameters.stop }}
          onChange={(value) => {
            handleChangeTimeRange(value);
          }}
          onChangeWindow={(value) => {
            handleChangeWindow(value);
          }}
          hideAbsoluteTime={false}
        />
        <Autocomplete
          size="small"
          options={windowOptions}
          value={windowOptions.find((option) => option.window === parameters.window)}
          getOptionLabel={(option) => _(option.label)}
          onChange={(_, value) => {
            handleChangeWindow(value.window);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={_(
                msg({
                  message: 'Promediar por periodo',
                  comment:
                    'Análisis de estaciones - etiqueta de campo de selección de ventana de promedio',
                }),
              )}
            />
          )}
          disableClearable
        />
        <Box display="flex" gap={1}>
          <Button
            onClick={handleReport}
            color="warning"
            variant="outlined"
            sx={{ justifySelf: 'flex-end' }}
          >
            <Trans comment="Análisis de estaciones - botón de reportar problema">
              Reportar un problema
            </Trans>{' '}
            <AlertIcon sx={{ ml: 2 }} />
          </Button>

          <Tooltip title={_(msg`Menú de dispositivo`)}>
            <span>
              <DeviceMenu CustomIcon={() => <GearIcon color="info" />} deviceId={device.id} />
            </span>
          </Tooltip>

          {hasAccess('Sec:Reports') && (
            <Tooltip title={_(msg`Generar reporte`)}>
              <IconButton
                onClick={() => {
                  const devices = Array.from(
                    new Set([parameters.deviceId, ...parameters.comparisonDeviceIds]),
                  ).filter((id) => id != null);

                  setCreateReportModalState({
                    open: true,
                    defaultData: {
                      devices: devices,
                      period: {
                        start: parameters.start,
                        stop: parameters.stop ?? null,
                      },
                      meanWindow: parameters.window,
                      metrics: getSelectedMetrics(telemetry, telemetry.availableFields),
                    },
                  });
                }}
              >
                <ReportsIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={_(msg`Descarga rápida de datos`)}>
            <IconButton
              onClick={() => {
                if (data) {
                  const { headers, rows } = dataToTable(data, i18n);
                  setDownloadDataModalState({ open: true, headers, rows });
                }
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

const METRIC_MAPPINGS: Array<{
  key: string | TelemetryKey;
  field: MetricField;
}> = [
  { key: TelemetryKey.AccumulatedVol, field: MetricField.AccumulatedVol },
  { key: TelemetryKey.Angle, field: MetricField.Angle },
  { key: TelemetryKey.DissolvedOxygen, field: MetricField.DissolvedOxygen },
  { key: TelemetryKey.DissolvedOxygenSaturation, field: MetricField.DissolvedOxygenSaturation },
  { key: TelemetryKey.Flow, field: MetricField.Flow },
  { key: TelemetryKey.FlowRaw, field: MetricField.FlowRaw },
  { key: TelemetryKey.Level, field: MetricField.Level },
  { key: TelemetryKey.LevelRaw, field: MetricField.LevelRaw },
  { key: TelemetryKey.OriginalDistance, field: MetricField.OriginalDistance },
  { key: TelemetryKey.Orp, field: MetricField.Orp },
  { key: TelemetryKey.Ph, field: MetricField.Ph },
  { key: TelemetryKey.Position, field: MetricField.Position },
  { key: TelemetryKey.GateSensorDistance, field: MetricField.GateSensorDistance },
  { key: TelemetryKey.GateSensorCurrent, field: MetricField.GateSensorCurrent },
  { key: TelemetryKey.SnowLevel, field: MetricField.SnowLevel },
  { key: TelemetryKey.SnowLevelNonCalibrated, field: MetricField.SnowLevelNonCalibrated },
  { key: TelemetryKey.SnowRadiation, field: MetricField.SnowRadiation },
  { key: TelemetryKey.SnowTemperature, field: MetricField.SnowTemperature },
  { key: TelemetryKey.SnowWaterEquivalent, field: MetricField.SnowWaterEquivalent },
  { key: TelemetryKey.SnowWeight, field: MetricField.SnowWeight },
  { key: TelemetryKey.Temperature, field: MetricField.Temperature },
  { key: TelemetryKey.TurbidityFnu, field: MetricField.TurbidityFnu },
  { key: TelemetryKey.TurbidityNtu, field: MetricField.TurbidityNtu },
  { key: TelemetryKey.Velocity, field: MetricField.Velocity },
  { key: TelemetryKey.VolumeDay, field: MetricField.VolumeDay },
  { key: TelemetryKey.VolumeHour, field: MetricField.VolumeHour },
  { key: TelemetryKey.VolumeMonth, field: MetricField.VolumeMonth },
];

const getSelectedMetrics = (
  telemetry: TelemetryData,
  availableFields: MetricField[],
): MetricConfig[] => {
  const fields = METRIC_MAPPINGS.filter(
    ({ key, field }) => telemetry[key] && availableFields.includes(field),
  ).map(({ field }) => {
    let unit = getMetricUnits(field).at(0)?.enum;

    if (field === MetricField.Flow && telemetry.flowUnit && isMetricUnit(telemetry.flowUnit)) {
      unit = telemetry.flowUnit;
    }

    if (
      [MetricField.Level, MetricField.LevelRaw].includes(field) &&
      telemetry.levelUnit &&
      isMetricUnit(telemetry.levelUnit)
    ) {
      unit = telemetry.levelUnit;
    }

    return { name: field, unit };
  });

  return fields;
};

export default Toolbar;
